import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, Trash2, Play, Star } from "lucide-react";
import { PomodoroStartMenu } from "./PomodoroTimer";
import RowTitle from "./itemrow/RowTitle";
import RowChips from "./itemrow/RowChips";

// A single task/habit/reminder/list row. Composition:
//   [toggle] [RowTitle + RowChips + historyStrip] [play] [delete]
// Title editing, the pencil action menu, and all chip popovers live in
// ./itemrow/ subcomponents — this file owns only the row frame + shared
// lead-picker open state (the alert chip and the pencil menu both open it).
export default function ItemRow({
    title,
    completed,
    priority,
    time,
    streak,
    dateChip,
    onToggle,
    onDelete,
    testId,
    showCountdown = false,
    onStartFocus,           // (focusMin, breakMin) => void
    isFocusing = false,     // dim+highlight when pomodoro is running for this item
    onEditTime,             // (nextHHMM) => Promise<void>  → enables inline time editor
    onEditTitle,            // (nextTitle) => Promise<void> → enables inline title editor
    editableTitle,          // Optional override for what shows in the title editor draft
    onEditSchedule,         // () => void → enables "Edit schedule" entry in the pencil menu (habits)
    onEditLead,             // (nextLeadMinutes) => Promise<void> → enables "Edit alert" entry (reminders)
    onEditPriority,         // (next: "low"|"medium"|"high") => Promise<void> → enables tap-to-edit on the priority chip (tasks)
    onOpenEdit,             // () => void → tapping the row's central content area opens the full edit sheet
    recurrence,             // { type, interval, unit?, day?, month? } | null — drives the ↻ indicator chip + tooltip
    avatarStyle = "feminine", // controls the colour of the recurrence indicator (rose vs indigo)
    currentLead,            // number | undefined → currently set lead_minutes (for highlighting)
    statusLabel,            // optional shame-safe pill (e.g. "rested for today" — replaces the time chip when set)
    recentlyFocused = false, // shows a soft "Recently focused 🌟" pill (tasks)
    coveredToday = false,    // habit-only: today was covered by Mochi's weekly pass → render ⭐ instead of green check
    onAskCover,              // habit-only: () => Promise — adds "Ask Mochi to cover" entry in the pencil menu
    historyStrip,            // optional ReactNode rendered on its own line under the chips (e.g. 7-day streak strip)
    mochiPick = false,       // task-only: true when this row is Mochi's current suggestion → golden glow + 🍡 badge
}) {
    const [menuOpen, setMenuOpen] = useState(false);
    // Shared between the pencil menu ("Edit alert") and the alert chip.
    const [leadPickerOpen, setLeadPickerOpen] = useState(false);
    const showPencilMenu = !!onEditTitle && (!!onStartFocus || !!onEditSchedule || !!onEditLead || !!onAskCover);

    return (
        <motion.div
            layout
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 8 }}
            transition={{ type: "spring", stiffness: 220, damping: 24 }}
            className={`group flex items-center gap-3 ${
                isFocusing ? "bg-terracotta-soft/30 ring-2 ring-terracotta" : mochiPick ? "bg-mustard-soft/50 ring-2 ring-mustard/60" : "bg-cream/80 hover:bg-cream"
            } rounded-2xl px-4 py-3 border border-warm-300/30 shadow-clay/30 transition-colors`}
            data-testid={testId}
        >
            <button
                onClick={onToggle}
                className={`shrink-0 w-7 h-7 rounded-full border-2 flex items-center justify-center transition-all ${
                    coveredToday
                        ? "bg-mustard-soft border-mustard text-mustard"
                        : completed
                        ? "bg-sage border-sage text-cream"
                        : "border-warm-300 hover:border-terracotta hover:bg-terracotta/10"
                }`}
                data-testid={`${testId}-toggle`}
                aria-label={coveredToday ? "Mochi covered this one" : "Toggle complete"}
                title={coveredToday ? "mochi covered this one 🍡" : undefined}
            >
                {coveredToday
                    ? <Star size={16} strokeWidth={2.5} fill="currentColor" />
                    : completed && <Check size={16} strokeWidth={3} />}
            </button>

            <div
                className={`flex-1 min-w-0 ${onOpenEdit && !completed ? "cursor-pointer" : ""}`}
                onClick={onOpenEdit && !completed ? (e) => {
                    // The chip + pencil buttons inside already stopPropagation.
                    // This handler only fires on taps that land in the empty
                    // title/chips area — exactly the "open edit sheet" intent.
                    e.stopPropagation();
                    onOpenEdit();
                } : undefined}
                role={onOpenEdit && !completed ? "button" : undefined}
                aria-label={onOpenEdit && !completed ? `Edit ${title}` : undefined}
                data-testid={`${testId}-open-edit`}
            >
                <RowTitle
                    testId={testId}
                    title={title}
                    editableTitle={editableTitle}
                    completed={completed}
                    onEditTitle={onEditTitle}
                    onStartFocus={onStartFocus}
                    onEditSchedule={onEditSchedule}
                    onEditLead={onEditLead}
                    onAskCover={onAskCover}
                    showPencilMenu={showPencilMenu}
                    currentLead={currentLead}
                    leadPickerOpen={leadPickerOpen}
                    setLeadPickerOpen={setLeadPickerOpen}
                />
                <RowChips
                    testId={testId}
                    completed={completed}
                    avatarStyle={avatarStyle}
                    recurrence={recurrence}
                    priority={priority}
                    onEditPriority={onEditPriority}
                    dateChip={dateChip}
                    mochiPick={mochiPick}
                    recentlyFocused={recentlyFocused}
                    statusLabel={statusLabel}
                    time={time}
                    onEditTime={onEditTime}
                    showCountdown={showCountdown}
                    onEditLead={onEditLead}
                    currentLead={currentLead}
                    openLeadPicker={() => setLeadPickerOpen(true)}
                    streak={streak}
                />
                {historyStrip && (
                    <div className="mt-0.5">{historyStrip}</div>
                )}
            </div>

            {onStartFocus && !completed && !showPencilMenu && (
                <div className="relative shrink-0">
                    <button
                        onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
                        className={`opacity-50 group-hover:opacity-100 ${isFocusing ? "text-terracotta opacity-100" : "text-warm-500 hover:text-terracotta"} transition-all p-1.5 rounded-full hover:bg-surface`}
                        data-testid={`${testId}-focus`}
                        aria-label="Start focus session"
                        title="Start focus session"
                    >
                        <Play size={14} />
                    </button>
                    {menuOpen && (
                        <>
                            <div className="fixed inset-0 z-30" onClick={() => setMenuOpen(false)} />
                            <PomodoroStartMenu
                                onClose={() => setMenuOpen(false)}
                                onStart={(focusMin, breakMin) => onStartFocus(focusMin, breakMin)}
                            />
                        </>
                    )}
                </div>
            )}

            {onDelete && (
                <button
                    onClick={onDelete}
                    className="opacity-40 group-hover:opacity-100 text-warm-500 hover:text-terracotta transition-all p-1.5 rounded-full hover:bg-surface"
                    data-testid={`${testId}-delete`}
                    aria-label="Delete"
                >
                    <Trash2 size={14} />
                </button>
            )}
        </motion.div>
    );
}
